// =============================================================================
// Shared Constants
// Single source of truth for all mappings and constants
// =============================================================================

// -----------------------------------------------------------------------------
// Month Mappings
// -----------------------------------------------------------------------------

export const MONTHS_SHORT = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

export const MONTHS_FULL = [
  "january", "february", "march", "april", "may", "june",
  "july", "august", "september", "october", "november", "december",
];

// All month names for tokenizer (full first for longest match)
export const ALL_MONTHS = [...MONTHS_FULL, ...MONTHS_SHORT];

// Month name to number mapping (1-indexed)
export const MONTH_TO_NUMBER: Record<string, number> = {
  jan: 1, january: 1,
  feb: 2, february: 2,
  mar: 3, march: 3,
  apr: 4, april: 4,
  may: 5,
  jun: 6, june: 6,
  jul: 7, july: 7,
  aug: 8, august: 8,
  sep: 9, sept: 9, september: 9,
  oct: 10, october: 10,
  nov: 11, november: 11,
  dec: 12, december: 12,
};

// -----------------------------------------------------------------------------
// Weekday Mappings
// -----------------------------------------------------------------------------

export const WEEKDAYS_FULL = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export const WEEKDAYS_SHORT = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export const ALL_WEEKDAYS = [...WEEKDAYS_FULL, ...WEEKDAYS_SHORT];

// Weekday name normalization (all forms to canonical)
export const WEEKDAY_TO_CANONICAL: Record<string, string> = {
  monday: "monday", mon: "monday",
  tuesday: "tuesday", tue: "tuesday",
  wednesday: "wednesday", wed: "wednesday",
  thursday: "thursday", thu: "thursday",
  friday: "friday", fri: "friday",
  saturday: "saturday", sat: "saturday",
  sunday: "sunday", sun: "sunday",
};

// Weekday to ISO number (Monday = 1, Sunday = 7)
export const WEEKDAY_TO_NUMBER: Record<string, number> = {
  monday: 1, mon: 1,
  tuesday: 2, tue: 2,
  wednesday: 3, wed: 3,
  thursday: 4, thu: 4,
  friday: 5, fri: 5,
  saturday: 6, sat: 6,
  sunday: 7, sun: 7,
};

// -----------------------------------------------------------------------------
// Time Unit Mappings
// -----------------------------------------------------------------------------

// Natural language units to expression units
export const UNIT_TO_EXPRESSION: Record<string, string> = {
  second: "s", seconds: "s",
  minute: "m", minutes: "m",
  hour: "h", hours: "h",
  day: "d", days: "d",
  week: "w", weeks: "w",
  month: "M", months: "M",
  year: "y", years: "y",
};

// Valid expression units
export const EXPRESSION_UNITS = ["s", "m", "h", "d", "w", "M", "y"];

// Round unit mappings (for /d, /w, /M, etc.)
export const ROUND_UNITS: Record<string, string> = {
  second: "s",
  minute: "m",
  hour: "h",
  day: "d",
  week: "w",
  month: "M",
  year: "y",
};

// -----------------------------------------------------------------------------
// Range Separators
// -----------------------------------------------------------------------------

// Word-based separators
export const WORD_SEPARATORS = ["to", "until", "till", "and"];

// Symbol-based separators
export const SYMBOL_SEPARATORS = ["->", "..", "~", "–", "—"]; // includes en-dash and em-dash

// -----------------------------------------------------------------------------
// Keywords
// -----------------------------------------------------------------------------

// Keywords that should be lowercase in output
export const LOWERCASE_KEYWORDS = new Set([
  "now", "today", "yesterday", "tomorrow",
  "last", "next", "this", "current", "past", "previous",
  "week", "month", "year", "day", "days", "hours", "minutes", "seconds",
  "hour", "minute", "second",
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
  "mon", "tue", "wed", "thu", "fri", "sat", "sun",
  "to", "at", "and", "from", "until", "till", "between",
  "ago", "in", "an", "a",
]);

// All recognized keywords for tokenizer
export const ALL_KEYWORDS = [
  // Time keywords
  "now", "today", "yesterday", "tomorrow", "noon",
  // Period modifiers
  "last", "next", "this", "previous",
  // Period keywords
  "week", "weeks", "month", "months", "year", "years",
  "day", "days", "hour", "hours", "minute", "minutes", "second", "seconds",
  // Natural language
  "ago", "in", "from", "at", "of",
  // Range keywords
  "between", "since", "before", "after",
  // Weekdays
  ...WEEKDAYS_FULL,
  ...WEEKDAYS_SHORT,
];
